import styled from 'styled-components';
import { StyledPaper, TitleRow } from '../../Styled/Styled';
import { CustomerDetailsContainer } from './Styled';
import { Commerce } from '@kotipizzagroup/kotipizza-shared-types';
import { DiscountDetail, OrderItemV2, OrderV2, ProductIngredientV2 } from '@kotipizzagroup/kotipizza-orders-api-client';
import {
  useAllIngredients,
  useAllProductCategories,
  useAllProducts,
  useAllProductSizes,
} from '../../../apis/products-api';
import { useMemo } from 'react';
import { ProductDto, ProductSizeDto } from '@kotipizzagroup/kotipizza-products-api-client';
import { baseDoughShortName } from '../constants';
import { formatEuro } from '../../utils/formatters';
import { useTranslation } from 'react-i18next';
import {
  baseDoughCategoryId,
  doughProductSizeIds,
  ovenProductSizeIds,
  pizzaBaseIngredientCategoryIds,
} from '../../utils/productAndIngredientUtils';
import { OrderProductsSkeleton } from './OrderDetailsSkeleton';
import ErrorIcon from '@mui/icons-material/Error';

interface ExtendedProductIngredientV2 extends ProductIngredientV2 {
  ingredientCategoryId: number;
  quantity?: number;
  recipeQuantity?: number;
}

interface ExtendedOrderItemV2 extends OrderItemV2 {
  sizeId: number;
  quantity: number;
  productCategoryId: number;
  productId?: number;
  ingredientCategoryId?: number;
  ingredients?: ExtendedProductIngredientV2[];
  productBaseInfo?: string;
}

export const OrderProducts: React.FC<{ order: OrderV2 }> = ({ order }) => {
  const { t } = useTranslation();
  const { data: products, isLoading: isProductsLoading } = useAllProducts();
  const { data: categories, isLoading: isCategoriesLoading } = useAllProductCategories();
  const { data: ingredients, isLoading: isIngredientsLoading } = useAllIngredients();
  const { data: productSizes, isLoading: isProductSizesLoading } = useAllProductSizes();

  const isLoading = isProductsLoading || isCategoriesLoading || isIngredientsLoading || isProductSizesLoading;

  const cartProductsPrice =
    order.shoppingCartPrice?.grandTotalWithoutShippingAndDiscounts &&
    order.shoppingCartPrice?.grandTotalWithoutShippingAndDiscounts >= 0
      ? order.shoppingCartPrice?.grandTotalWithoutShippingAndDiscounts
      : (order.shoppingCartPrice?.grandTotal ? order.shoppingCartPrice?.grandTotal : 0) +
        (order.shoppingCartPrice?.discountsSum ? order.shoppingCartPrice?.discountsSum : 0) -
        (order.shoppingCartPrice?.shippingCharges ? order.shoppingCartPrice?.shippingCharges : 0) -
        (order.shoppingCartPrice?.phoneOrderFee ? order.shoppingCartPrice?.phoneOrderFee : 0) -
        (order.shoppingCartPrice?.deliveryServiceFee ? order.shoppingCartPrice?.deliveryServiceFee : 0);

  const dipsAsProducts = useMemo(
    () =>
      order?.shoppingCartPrice?.orderItemsV2?.map((item) => {
        const dips = getDips(products);
        const dipFromIngredients = dips?.find((dip) => {
          return item?.ingredients?.find((ingredient) => {
            return dip.ingredients && dip.ingredients[0].ingredientId === ingredient.ingredientId;
          });
        });

        if (dipFromIngredients && item.productCategoryId !== Commerce.ProductCategory.DIP) {
          return {
            ...dipFromIngredients,
            quantity: 1,
            productBaseInfo: '',
            productCategoryName: dipFromIngredients.productCategoryName,
            ingredients: [],
            sizeId: dipFromIngredients.productCategoryId,
          };
        }
      }),
    [products, order]
  );

  const filteredProducts = useMemo(
    () =>
      order?.shoppingCartPrice?.orderItemsV2?.map((item) => {
        const dips = getDips(products);

        const dipIDs = dips
          ? dips.map((dip) => {
              if (dip.ingredients) {
                return { productId: dip.productId, ingredientid: dip?.ingredients[0]?.ingredientId };
              }
              return { productId: dip.productId, ingredientid: null };
            })
          : [];

        const filteredIngredients: ExtendedProductIngredientV2[] =
          item?.ingredients
            ?.filter(
              (ingredient) =>
                item.productCategoryId !== Commerce.ProductCategory.DIP &&
                item.productCategoryId !== Commerce.ProductCategory.DRINK &&
                item.productCategoryId !== Commerce.ProductCategory.SNACKS &&
                item.productCategoryId !== Commerce.ProductCategory.ICECREAM &&
                !dipIDs.find((dip) => dip && dip.ingredientid === ingredient.ingredientId)
            )
            .map((item) => {
              const ingredientCategory =
                ingredients?.find((ingredient) => ingredient.ingredientId === item.ingredientId)
                  ?.ingredientCategoryId ?? 0;
              return { ...item, ingredientCategoryId: ingredientCategory };
            }) ?? [];

        const productCategory = categories?.find((category) => category.productCategoryId === item.productCategoryId);

        const itemSize =
          item.sizeId !== undefined && doughProductSizeIds.includes(item.sizeId) && productSizes !== undefined
            ? getSizeName(item.sizeId, productSizes)
            : '';

        const baseDough = filteredIngredients?.find(
          (ingredient) =>
            ingredient.ingredientCategoryId === baseDoughCategoryId && ingredient.quantity && ingredient?.quantity > 0
        );

        const baseInfo = (itemSize && getBaseInfo(itemSize?.toString(), baseDough))?.toString();

        return {
          ...item,
          ingredients: sortIngredients(filteredIngredients),
          productCategoryName: productCategory?.productCategoryName,
          productBaseInfo: baseInfo,
          quantity: item.quantity ?? 0,
          productCategoryId: item.productCategoryId ?? 0,
          sizeId: item.sizeId ?? 0,
        };
      }),
    [order, products, categories, productSizes, ingredients]
  );

  const orderProducts = useMemo(() => {
    if (dipsAsProducts) {
      dipsAsProducts.forEach((product) => {
        if (product) {
          filteredProducts?.push(product);
        }
      });
    }
    if (filteredProducts) {
      return sortAndReorderProducts(combineByHash(filteredProducts), ovenProductSizeIds);
    }
  }, [filteredProducts, dipsAsProducts]);

  // Same logic as the order receipt: group the details by campaign and count how many products the campaign
  // was applied to. Counting the order rows instead reports e.g. two identical lunch combos as one, since
  // identical products are combined into a single row carrying a quantity.
  const orderDiscounts = useMemo(() => {
    const discountDetails = order.shoppingCartPrice?.discountDetails;
    if (!discountDetails) return undefined;

    const discountsByCampaign = new Map<string, DiscountDetail[]>();

    discountDetails.forEach((discount, index) => {
      const key = discount.campaign?.campaignId ?? `noCampaign-${index}`;
      discountsByCampaign.set(key, [...(discountsByCampaign.get(key) ?? []), discount]);
    });

    return Array.from(discountsByCampaign, ([, campaignDiscounts]) => ({
      ...campaignDiscounts[0],
      discountInEUR: campaignDiscounts.reduce((total, discount) => total + (discount.discountInEUR ?? 0), 0),
      quantity:
        getAppliedQuantity(order.shoppingCartPrice?.orderItemsV2, campaignDiscounts[0].campaign?.campaignId) ||
        campaignDiscounts.length,
    }));
  }, [order]);

  // Only while the product data is actually on its way: an order whose rows resolved to nothing is not
  // loading, and used to sit under a spinner forever.
  if (isLoading) {
    return <OrderProductsSkeleton title={t('Products')} />;
  }

  return (
    <>
      <StyledPaper variant="outlined">
        <CustomerDetailsContainer>
          <TitleRow>
            <h3>{t('Products')}</h3>
          </TitleRow>
          {orderProducts?.map((product, index) => (
            <ProductBlock key={`${index}_${product.productId}`}>
              <ProductRow>
                <CategoryAndSize> {product.productBaseInfo}</CategoryAndSize>
                <Quantity>{product.quantity && product.quantity > 1 ? product.quantity + ' x' : ''}</Quantity>
                <PriceDetails>
                  <Product>{product.name}</Product>
                  <Price>{product.totalPrice ? formatEuro(product.totalPrice) : ''}</Price>
                </PriceDetails>
              </ProductRow>
              {droppedBaseCategories
                .filter(({ categoryId }) => isBaseCategoryDropped(categoryId, product.ingredients))
                .map(({ categoryId, translationKey }) => (
                  <IngredientChangeRow key={categoryId}>
                    <BaseChangeIngredientBox>
                      <ErrorIcon />
                      {t(translationKey)}
                    </BaseChangeIngredientBox>
                  </IngredientChangeRow>
                ))}
              {product.ingredients?.map((ingredient: ExtendedProductIngredientV2, index) => (
                <div key={`${index}_${ingredient.ingredientId}`}>
                  {ingredient.quantity !== undefined &&
                    ingredient.recipeQuantity !== undefined &&
                    ingredient.name !== null &&
                    ingredient.quantity !== ingredient.recipeQuantity && (
                      <>
                        {ingredient.ingredientCategoryId &&
                        pizzaBaseIngredientCategoryIds.includes(ingredient.ingredientCategoryId) ? (
                          <>
                            {ingredient.quantity > ingredient.recipeQuantity && (
                              <IngredientChangeRow>
                                <BaseChangeIngredientBox>
                                  <ErrorIcon />
                                  <span>
                                    {t('Change: ')}
                                    {ingredient.name}
                                  </span>
                                  {(ingredient.ingredientCategoryId === Commerce.IngredientCategory.CHEESE ||
                                    ingredient.ingredientCategoryId === Commerce.IngredientCategory.BASE_SAUCE) &&
                                    t(' for the base')}
                                </BaseChangeIngredientBox>
                              </IngredientChangeRow>
                            )}
                          </>
                        ) : (
                          <>
                            {ingredient.quantity > ingredient.recipeQuantity ? (
                              <IngredientChangeRow>
                                <AddedIngredientBox>
                                  + {ingredient.name}{' '}
                                  {Math.abs(ingredient.recipeQuantity - ingredient.quantity) > 1 &&
                                    ' x' + Math.abs(ingredient.recipeQuantity - ingredient.quantity)}
                                </AddedIngredientBox>
                              </IngredientChangeRow>
                            ) : (
                              <IngredientChangeRow>
                                {ingredient.name && (
                                  <RemovedIngredientBox>
                                    — {ingredient.name}{' '}
                                    {ingredient.recipeQuantity > 1 &&
                                      ' x' + Math.abs(ingredient.recipeQuantity - ingredient.quantity)}
                                  </RemovedIngredientBox>
                                )}
                              </IngredientChangeRow>
                            )}
                          </>
                        )}
                      </>
                    )}
                </div>
              ))}
            </ProductBlock>
          ))}
        </CustomerDetailsContainer>
        <PricingDetailsContainer>
          <PriceDetails>
            <PriceCategory>{t('Products')}</PriceCategory>
            <Price isBold={true}>{formatEuro(cartProductsPrice)}</Price>
          </PriceDetails>
          {order.shoppingCartPrice?.shippingCharges ? (
            <PriceDetails>
              <PriceCategory>{t('Delivery')}</PriceCategory>
              <Price isBold={true}>{formatEuro(order.shoppingCartPrice?.shippingCharges)}</Price>
            </PriceDetails>
          ) : null}
          {order.shoppingCartPrice?.deliveryServiceFee ? (
            <PriceDetails>
              <PriceCategory>{t('Service fee')}</PriceCategory>
              <Price isBold={true}>{formatEuro(order.shoppingCartPrice?.deliveryServiceFee)}</Price>
            </PriceDetails>
          ) : null}
          {order.shoppingCartPrice?.phoneOrderFee ? (
            <PriceDetails>
              <PriceCategory>{t('Phonefee')}</PriceCategory>
              <Price isBold={true}>{formatEuro(order.shoppingCartPrice?.phoneOrderFee)}</Price>
            </PriceDetails>
          ) : null}
          {order.shoppingCartPrice?.discountsSum ? (
            <PriceDetails>
              <PriceCategory>{t('Discounts')}</PriceCategory>
              <Price isDiscount={true} isBold={true}>
                -{formatEuro(order.shoppingCartPrice?.discountsSum)}
              </Price>
            </PriceDetails>
          ) : null}
          <PriceDetails>
            <PriceCategory>{t('Paid')}</PriceCategory>
            <Price isBold={true}>
              {order.shoppingCartPrice?.grandTotal !== undefined
                ? formatEuro(order.shoppingCartPrice.grandTotal)
                : null}
            </Price>
          </PriceDetails>
        </PricingDetailsContainer>
      </StyledPaper>
      {orderDiscounts && orderDiscounts.length > 0 && (
        <StyledPaper variant="outlined">
          <TitleRow>
            <h3>{t('Discounts breakdown')}</h3>
          </TitleRow>
          <PricingDetailsContainer>
            {orderDiscounts.map((discount, index) => {
              const label = discount.campaign?.name ?? discount.campaign?.campaignCategory ?? '';
              return (
                <PriceDetails key={index}>
                  <DiscountPriceCategory>
                    {discount.quantity && discount.quantity > 1 ? `${discount.quantity}x ${label}` : label}
                  </DiscountPriceCategory>
                  <Price isDiscount={true}>-{formatEuro(discount.discountInEUR)}</Price>
                </PriceDetails>
              );
            })}
          </PricingDetailsContainer>
        </StyledPaper>
      )}
    </>
  );
};

const getAppliedQuantity = (orderItems: OrderItemV2[] | undefined, campaignId: string | undefined) =>
  orderItems?.reduce(
    (total, item) =>
      total +
      (item.discounts
        ?.filter((discount) => discount.campaignId === campaignId)
        .reduce((itemTotal, discount) => itemTotal + (discount.appliedQuantity ?? item.quantity ?? 1), 0) ?? 0),
    0
  ) ?? 0;

const generateHash = (productId: number, ingredients: ExtendedProductIngredientV2[]) =>
  `${productId}-${ingredients
    .map((ingredient) => `${ingredient.name}:${ingredient.quantity}`)
    .sort()
    .join('|')}`;

const combineByHash = (products: ExtendedOrderItemV2[]) => {
  const map = new Map();

  products.forEach((product) => {
    if (product.productId) {
      const hash = generateHash(product.productId, product.ingredients || []);

      if (map.has(hash)) {
        map.set(hash, { ...map.get(hash), quantity: (map.get(hash).quantity || 0) + (product.quantity || 0) });
      } else {
        map.set(hash, { ...product });
      }
    }
  });

  return Array.from(map.values());
};

const sortAndReorderProducts = (products: ExtendedOrderItemV2[], ovenProductSizeIds: number[]) => {
  const priorityItems = ovenProductSizeIds
    .map((sizeId) => products.filter((item) => item.sizeId === sizeId))
    .flat()
    .sort((a, b) => a.sizeId - b.sizeId)
    .sort((a, b) => b.quantity - a.quantity)
    .sort((a, b) => a.productCategoryId - b.productCategoryId);

  const remainingItems = products
    .filter((item) => !ovenProductSizeIds.includes(item.sizeId))
    .sort((a, b) => a.sizeId - b.sizeId)
    .sort((a, b) => b.quantity - a.quantity)
    .sort((a, b) => a.productCategoryId - b.productCategoryId);

  return [...priorityItems, ...remainingItems];
};

const getBaseInfo = (size?: string, dough?: ExtendedProductIngredientV2) => {
  const baseSize = size === 'Medium' ? 'M' : size === 'Large' ? 'L' : size;

  const baseDough = dough?.ingredientId && baseDoughShortName[dough?.ingredientId];
  if (baseDough) return baseDough + ' ' + baseSize;
  return baseSize;
};

// Neither "no cheese" nor "no base sauce" is an ingredient of its own, so dropping one only leaves the
// recipe's own cheese or sauce sitting at quantity 0 - there is no row for the ingredient map to turn into a
// base change. Detect it per product instead: the recipe called for the category, and nothing in that
// category survived on the ordered product.
const isBaseCategoryDropped = (categoryId: number, ingredients?: ExtendedProductIngredientV2[]) => {
  const inCategory = ingredients?.filter((ingredient) => ingredient.ingredientCategoryId === categoryId);
  if (!inCategory?.length) return false;

  return (
    inCategory.some((ingredient) => (ingredient.recipeQuantity ?? 0) > 0) &&
    !inCategory.some((ingredient) => (ingredient.quantity ?? 0) > 0)
  );
};

const droppedBaseCategories = [
  { categoryId: Commerce.IngredientCategory.CHEESE, translationKey: 'Change: No cheese for the base' },
  { categoryId: Commerce.IngredientCategory.BASE_SAUCE, translationKey: 'Change: No base sauce' },
];

const sortIngredients = (ingredients: ExtendedProductIngredientV2[] | undefined) => {
  return ingredients?.sort((a, b) => {
    // First, prioritize pizza base ingredients  - base changes
    const aIsBase = pizzaBaseIngredientCategoryIds.includes(a.ingredientCategoryId);
    const bIsBase = pizzaBaseIngredientCategoryIds.includes(b.ingredientCategoryId);
    if (aIsBase && !bIsBase) return -1;
    if (!aIsBase && bIsBase) return 1;

    // Second, prioritize items where quantity > recipeQuantity - added ingredients
    if (a.quantity && a.recipeQuantity && b.quantity && b.recipeQuantity) {
      const aHasMore = a.quantity > a.recipeQuantity;
      const bHasMore = b.quantity > b.recipeQuantity;
      if (aHasMore && !bHasMore) return -1;
      if (!aHasMore && bHasMore) return 1;

      // Last, prioritize items where recipeQuantity > quantity - removed ingredients
      const aHasLess = a.recipeQuantity > a.quantity;
      const bHasLess = b.recipeQuantity > b.quantity;
      if (aHasLess && !bHasLess) return 1;
      if (!aHasLess && bHasLess) return -1;
    }
    return 0;
  });
};

const getSizeName = (sizeId: number, productSizes: ProductSizeDto[]): string => {
  const productSize = productSizes.find((size) => size.productSizeId === sizeId);
  if (!productSize) return '';

  return productSize.name;
};

const getDips = (products: ProductDto[] | undefined) => {
  const dips = products?.filter((product) => {
    if (product.productCategoryId === Commerce.ProductCategory.DIP) {
      return product.productId;
    }
  });
  return dips;
};

// Column widths the product row is built from, reused by the ingredient indent below so the
// add/remove chips line up under the product name at any width.
const CATEGORY_WIDTH_MOBILE = '3.5rem';
const QUANTITY_WIDTH = '1.5rem';
const ROW_GAP = '8px';

const InformationRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  padding: 8px 0px 8px;
  align-items: baseline;
`;

const Quantity = styled.div`
  flex: 0 0 auto;
  min-width: ${QUANTITY_WIDTH};
  font-weight: bold;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    width: 5%;
  }
`;
const CategoryAndSize = styled.div`
  flex: 0 0 auto;
  min-width: ${CATEGORY_WIDTH_MOBILE};

  ${({ theme }) => theme.breakpoints.up('sm')} {
    width: 10%;
    min-width: 6rem;
  }
`;

// `flex: 1` instead of `width: 75%`: the parent is `justify-content: space-between`, so the price was
// already pinned right, but a long product name no longer overflows a narrow screen.
const Product = styled.div`
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const PriceCategory = styled.div`
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const DiscountPriceCategory = styled(PriceCategory)`
  font-weight: normal;
`;

const ProductRow = styled(InformationRow)`
  flex-wrap: nowrap;
  gap: ${ROW_GAP};
  border-top: solid lightgray 1px;
  padding-top: 16px;
`;

const ProductBlock = styled.div`
  padding-bottom: 8px;
`;

const PricingDetailsContainer = styled.div`
  padding-top: 8px;
`;

const Price = styled.div<{ isDiscount?: boolean; isBold?: boolean }>`
  flex: 0 0 auto;
  white-space: nowrap;
  color: ${({ theme, isDiscount }) => (isDiscount ? theme.palette.warning.main : theme.palette.text.primary)};
  font-weight: ${({ isBold }) => (isBold ? 'bold' : 'normal')};
`;

// `width` applies to the standalone pricing-summary rows; `flex: 1` applies when this sits inside a
// ProductRow, where the width is ignored anyway.
const PriceDetails = styled.div`
  width: 100%;
  flex: 1;
  min-width: 0;
  display: flex;
  justify-content: space-between;
  gap: ${ROW_GAP};
  border-top: none;
`;

// Indented by the same tokens the product row is built from, so the chips sit under the product name
// instead of being pushed off the right edge of a phone by a percentage indent.
const IngredientChangeRow = styled(InformationRow)`
  gap: 4px;
  padding-top: 0px;
  padding-left: calc(${CATEGORY_WIDTH_MOBILE} + ${QUANTITY_WIDTH} + ${ROW_GAP} * 2);

  ${({ theme }) => theme.breakpoints.up('sm')} {
    padding-left: 22%;
  }
`;

const AddedIngredientBox = styled.div`
  color: ${({ theme }) => theme.palette.success.dark};
  background-color: ${({ theme }) => theme.palette.success.light};
  padding: 4px 10px;
  font-weight: bold;
  border-radius: 4px;
  overflow-wrap: anywhere;
`;

const RemovedIngredientBox = styled(AddedIngredientBox)`
  color: ${({ theme }) => theme.palette.error.main};
  background-color: ${({ theme }) => theme.palette.error.light};
`;

const BaseChangeIngredientBox = styled(AddedIngredientBox)`
  color: #2f194c;
  background-color: #e1cafd;
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    flex-shrink: 0;
    font-size: 1.125rem;
  }
`;
